import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import api from "../lib/api";
import { createSupportTicket, getMySupportTickets } from "../lib/supportApi";

const SUPPORT_TYPES = [
  {
    key: "problem",
    title: "Report a Problem",
    subtitle:
      "Deployment failures, bugs, payment issues, verification issues, or platform errors.",
    issueType: "DEPLOYMENT_ERROR",
    defaultPriority: "HIGH",
  },
  {
    key: "billing",
    title: "Billing / Plan Help",
    subtitle:
      "Upgrade questions, Pro access requests, pricing clarification, or billing support.",
    issueType: "PAYMENT_ISSUE",
    defaultPriority: "MEDIUM",
  },
  {
    key: "feature",
    title: "Request a Feature",
    subtitle:
      "Suggest new features, workflow improvements, or better tooling for your team.",
    issueType: "FEATURE_REQUEST",
    defaultPriority: "MEDIUM",
  },
  {
    key: "question",
    title: "Ask a Question",
    subtitle:
      "Get help using Krionex or understanding product and deployment behavior.",
    issueType: "OTHER",
    defaultPriority: "MEDIUM",
  },
];

const TOKEN_TYPE_OPTIONS = [
  { value: "", label: "Select token standard" },
  { value: "ERC20", label: "ERC-20" },
  { value: "ERC721", label: "ERC-721" },
  { value: "ERC1155", label: "ERC-1155" },
];

const HELP_CENTER_DRAFT_KEY = "Krionex_help_center_draft_v1";

function formatDate(value) {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function humanizeIssueType(issueType) {
  switch (issueType) {
    case "DEPLOYMENT_ERROR":
      return "Problem Report";
    case "FEATURE_REQUEST":
      return "Feature Request";
    case "PAYMENT_ISSUE":
      return "Payment Issue";
    case "NETWORK_ERROR":
      return "Network Issue";
    case "CONTRACT_VERIFICATION":
      return "Verification Issue";
    case "BUG_REPORT":
      return "Bug Report";
    default:
      return "General Help";
  }
}

function humanizeStatus(status) {
  switch (status) {
    case "OPEN":
      return "Open";
    case "IN_REVIEW":
      return "In Review";
    case "WAITING_FOR_USER":
      return "Waiting for You";
    case "RESOLVED":
      return "Resolved";
    case "CLOSED":
      return "Closed";
    default:
      return status;
  }
}

function normalizeOptional(value) {
  return value.trim() || null;
}

function buildSupportDescription({
  userDescription,
  networkLabel,
  tokenType,
  tokenName,
  tokenSymbol,
  contractAddress,
  deploymentId,
  transactionHash,
  tokenId,
  amount,
  decimals,
  baseURI,
  advancedData,
}) {
  const sections = [];

  sections.push(userDescription.trim());

  const technicalContext = [
    `Network: ${networkLabel || "-"}`,
    `Token Standard: ${tokenType || "-"}`,
    `Token Name: ${tokenName.trim() || "-"}`,
    `Contract Address: ${contractAddress.trim() || "-"}`,
    `Deployment ID: ${deploymentId.trim() || "-"}`,
    `Transaction Hash: ${transactionHash.trim() || "-"}`,
  ];

  if (tokenType === "ERC20" || tokenType === "ERC721") {
    technicalContext.splice(3, 0, `Token Symbol: ${tokenSymbol.trim() || "-"}`);
  }

  if (tokenType === "ERC20") {
    technicalContext.push(`Amount Involved: ${amount.trim() || "-"}`);
    technicalContext.push(`Decimals: ${decimals.trim() || "-"}`);
  }

  if (tokenType === "ERC721") {
    technicalContext.push(`Token ID: ${tokenId.trim() || "-"}`);
    technicalContext.push(`Metadata Base URI: ${baseURI.trim() || "-"}`);
  }

  if (tokenType === "ERC1155") {
    technicalContext.push(`Token ID: ${tokenId.trim() || "-"}`);
    technicalContext.push(`Amount: ${amount.trim() || "-"}`);
    technicalContext.push(`Base URI / Metadata Template: ${baseURI.trim() || "-"}`);
    technicalContext.push(`Advanced Data: ${advancedData.trim() || "-"}`);
  }

  sections.push("Technical Context:");
  sections.push(technicalContext.join("\n"));

  return sections.join("\n\n");
}

function cn(...values) {
  return values.filter(Boolean).join(" ");
}

function Surface({ className = "", children }) {
  return (
    <div
      className={cn(
        "rounded-[28px] border border-slate-200/80 bg-white shadow-[0_18px_55px_rgba(15,23,42,0.06)]",
        className,
      )}
    >
      {children}
    </div>
  );
}

function SectionCard({ title, subtitle, right, children, className = "" }) {
  return (
    <Surface className={cn("overflow-hidden p-6 sm:p-7", className)}>
      <div className="mb-6 flex flex-col gap-4 border-b border-slate-100 pb-5 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="inline-flex rounded-full border border-[#E7D8AC] bg-[#FFF9EA] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#8A6A1F]">
            Support
          </div>

          <h2 className="mt-3 text-xl font-semibold tracking-tight text-slate-950">
            {title}
          </h2>

          {subtitle ? (
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
              {subtitle}
            </p>
          ) : null}
        </div>

        {right ? <div className="shrink-0">{right}</div> : null}
      </div>

      {children}
    </Surface>
  );
}

function InlineNotice({ title, subtitle, tone = "neutral" }) {
  const toneClass =
    tone === "warning"
      ? "border-[#E7D8AC] bg-[#FFF9EA] text-[#6F5318]"
      : tone === "error"
        ? "border-rose-200 bg-rose-50 text-rose-900"
        : tone === "success"
          ? "border-emerald-200 bg-emerald-50 text-emerald-900"
          : "border-slate-200 bg-slate-50 text-slate-900";

  return (
    <div className={cn("rounded-2xl border px-4 py-3.5", toneClass)}>
      <div className="text-sm font-semibold">{title}</div>
      {subtitle ? (
        <div className="mt-1 text-xs leading-5 opacity-80">{subtitle}</div>
      ) : null}
    </div>
  );
}

function Badge({ children, tone = "neutral" }) {
  const toneClass =
    tone === "success"
      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
      : tone === "warning"
        ? "border-[#E7D8AC] bg-[#FFF9EA] text-[#8A6A1F]"
        : tone === "brand"
          ? "border-[#E7D8AC] bg-[#FFF9EA] text-[#7A5A17]"
          : tone === "violet"
            ? "border-slate-300 bg-slate-100 text-slate-700"
            : "border-slate-200 bg-white text-slate-600";

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold",
        toneClass,
      )}
    >
      {children}
    </span>
  );
}

function Label({ children }) {
  return (
    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
      {children}
    </label>
  );
}

function Input({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={cn(
        "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-950 outline-none transition",
        "placeholder:text-slate-400 focus:border-[#C59B45] focus:ring-4 focus:ring-[#E6C978]/20",
        "disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500",
        className,
      )}
    />
  );
}

function Select({ className = "", children, ...props }) {
  return (
    <select
      {...props}
      className={cn(
        "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-950 outline-none transition",
        "focus:border-[#C59B45] focus:ring-4 focus:ring-[#E6C978]/20",
        "disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500",
        className,
      )}
    >
      {children}
    </select>
  );
}

function Textarea({ className = "", ...props }) {
  return (
    <textarea
      {...props}
      className={cn(
        "min-h-[150px] w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-950 outline-none transition",
        "placeholder:text-slate-400 focus:border-[#C59B45] focus:ring-4 focus:ring-[#E6C978]/20",
        className,
      )}
    />
  );
}

function PrimaryButton({ className = "", children, ...props }) {
  return (
    <button
      {...props}
      className={cn(
        "inline-flex items-center justify-center rounded-2xl bg-[#050713] px-4 py-3 text-sm font-semibold text-white shadow-[0_16px_35px_rgba(5,7,19,0.20)] transition",
        "hover:-translate-y-0.5 hover:bg-[#111827] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0",
        className,
      )}
    >
      {children}
    </button>
  );
}


function SupportTypeCard({ item, active, onClick }) {
  const iconMap = {
    problem: "!",
    billing: "$",
    feature: "+",
    question: "?",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "group rounded-[24px] border p-5 text-left shadow-sm transition-all duration-200",
        active
          ? "border-[#D8B85E] bg-[#FFF9EA] shadow-[0_18px_45px_rgba(197,155,69,0.16)]"
          : "border-slate-200 bg-white hover:-translate-y-0.5 hover:border-[#E7D8AC] hover:shadow-[0_18px_45px_rgba(15,23,42,0.08)]",
      )}
    >
      <div className="flex items-start gap-4">
        <div
          className={cn(
            "flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border text-sm font-bold",
            active
              ? "border-[#D8B85E] bg-[#050713] text-[#E6C978]"
              : "border-slate-200 bg-slate-50 text-slate-700 group-hover:border-[#E7D8AC] group-hover:bg-[#FFF9EA]",
          )}
        >
          {iconMap[item.key] || "?"}
        </div>

        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <div className="text-sm font-semibold text-slate-950">
              {item.title}
            </div>

            {active ? (
              <span className="rounded-full bg-[#050713] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#E6C978]">
                Active
              </span>
            ) : null}
          </div>

          <div className="mt-2 text-sm leading-6 text-slate-500">
            {item.subtitle}
          </div>
        </div>
      </div>
    </button>
  );
}

export default function HelpCenter() {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const [supportType, setSupportType] = useState("problem");
  const [walletAddress, setWalletAddress] = useState("");
  const [walletEditedManually, setWalletEditedManually] = useState(false);
  const [network, setNetwork] = useState("");
  const [availableNetworks, setAvailableNetworks] = useState([]);

  const [subject, setSubject] = useState("");
  const [subjectEditedManually, setSubjectEditedManually] = useState(false);
  const [description, setDescription] = useState("");

  const [tokenType, setTokenType] = useState("");
  const [tokenName, setTokenName] = useState("");
  const [tokenSymbol, setTokenSymbol] = useState("");
  const [contractAddress, setContractAddress] = useState("");
  const [deploymentId, setDeploymentId] = useState("");
  const [transactionHash, setTransactionHash] = useState("");

  const [tokenId, setTokenId] = useState("");
  const [amount, setAmount] = useState("");
  const [decimals, setDecimals] = useState("");
  const [baseURI, setBaseURI] = useState("");
  const [advancedData, setAdvancedData] = useState("0x");

  const [requests, setRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [loadingNetworks, setLoadingNetworks] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const selectedSupportType = useMemo(() => {
    return (
      SUPPORT_TYPES.find((item) => item.key === supportType) || SUPPORT_TYPES[0]
    );
  }, [supportType]);

  const isBillingSupport = supportType === "billing";

  const selectedNetworkMeta = useMemo(() => {
    return availableNetworks.find((item) => item.key === network) || null;
  }, [availableNetworks, network]);

  const selectedNetworkLabel = useMemo(() => {
    return selectedNetworkMeta?.label || network || "";
  }, [selectedNetworkMeta, network]);

  useEffect(() => {
    const syncWalletFromStorage = () => {
      if (!isAuthenticated) {
        setWalletAddress("");
        return;
      }

      const savedWallet =
        localStorage.getItem("chainforge_wallet_address") || "";

      if (!walletEditedManually) {
        setWalletAddress(savedWallet);
      }
    };

    syncWalletFromStorage();

    window.addEventListener("focus", syncWalletFromStorage);
    window.addEventListener("storage", syncWalletFromStorage);
    window.addEventListener("chainforge:wallet-changed", syncWalletFromStorage);

    return () => {
      window.removeEventListener("focus", syncWalletFromStorage);
      window.removeEventListener("storage", syncWalletFromStorage);
      window.removeEventListener(
        "chainforge:wallet-changed",
        syncWalletFromStorage,
      );
    };
  }, [walletEditedManually, isAuthenticated]);


  useEffect(() => {
    let cancelled = false;

    async function checkAuthState() {
      const token = localStorage.getItem("cf_access_token");

      if (!token) {
        if (!cancelled) {
          setIsAuthenticated(false);
          setRequests([]);
          setWalletAddress("");
          setWalletEditedManually(false);
          setSupportType("problem");
          setNetwork("");
          setSubject("");
          setSubjectEditedManually(false);
          setDescription("");
          setTokenType("");
          setTokenName("");
          setTokenSymbol("");
          setContractAddress("");
          setDeploymentId("");
          setTransactionHash("");
          setTokenId("");
          setAmount("");
          setDecimals("");
          setBaseURI("");
          setAdvancedData("0x");
          setFormError("");
          setSuccessMessage("");
          localStorage.removeItem(HELP_CENTER_DRAFT_KEY);
        }
        return;
      }

      try {
        await api.get("/auth/me");

        if (!cancelled) {
          setIsAuthenticated(true);
        }
      } catch {
        if (!cancelled) {
          setIsAuthenticated(false);
          setRequests([]);
          setWalletAddress("");
          setWalletEditedManually(false);
          setSupportType("problem");
          setNetwork("");
          setSubject("");
          setSubjectEditedManually(false);
          setDescription("");
          setTokenType("");
          setTokenName("");
          setTokenSymbol("");
          setContractAddress("");
          setDeploymentId("");
          setTransactionHash("");
          setTokenId("");
          setAmount("");
          setDecimals("");
          setBaseURI("");
          setAdvancedData("0x");
          setFormError("");
          setSuccessMessage("");
          localStorage.removeItem(HELP_CENTER_DRAFT_KEY);
        }
      }
    }

    checkAuthState();

    const syncAuthState = () => {
      checkAuthState();
    };

    window.addEventListener("focus", syncAuthState);
    window.addEventListener("storage", syncAuthState);
    window.addEventListener("chainforge:wallet-changed", syncAuthState);

    return () => {
      cancelled = true;
      window.removeEventListener("focus", syncAuthState);
      window.removeEventListener("storage", syncAuthState);
      window.removeEventListener("chainforge:wallet-changed", syncAuthState);
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    try {
      const rawDraft = localStorage.getItem(HELP_CENTER_DRAFT_KEY);
      if (!rawDraft) return;

      const draft = JSON.parse(rawDraft);

      if (draft.supportType) setSupportType(draft.supportType);
      if (draft.walletAddress) {
        setWalletAddress(draft.walletAddress);
        setWalletEditedManually(Boolean(draft.walletEditedManually));
      }
      if (draft.network) setNetwork(draft.network);
      if (draft.subject) {
        setSubject(draft.subject);
        setSubjectEditedManually(Boolean(draft.subjectEditedManually));
      }
      if (draft.description) setDescription(draft.description);
      if (draft.tokenType) setTokenType(draft.tokenType);
      if (draft.tokenName) setTokenName(draft.tokenName);
      if (draft.tokenSymbol) setTokenSymbol(draft.tokenSymbol);
      if (draft.contractAddress) setContractAddress(draft.contractAddress);
      if (draft.deploymentId) setDeploymentId(draft.deploymentId);
      if (draft.transactionHash) setTransactionHash(draft.transactionHash);
      if (draft.tokenId) setTokenId(draft.tokenId);
      if (draft.amount) setAmount(draft.amount);
      if (draft.decimals) setDecimals(draft.decimals);
      if (draft.baseURI) setBaseURI(draft.baseURI);
      if (draft.advancedData) setAdvancedData(draft.advancedData);
    } catch (error) {
      console.error("Failed to restore Help Center draft:", error);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    let cancelled = false;

    async function loadNetworks() {
      setLoadingNetworks(true);
      try {
        const res = await api.get("/networks", {
          params: {
            onlyConfigured: true,
            includeTestnets: true,
            enabledOnly: true,
          },
        });

        const networks = Array.isArray(res?.data?.networks)
          ? res.data.networks
          : [];

        if (!cancelled) {
          setAvailableNetworks(networks);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to load support networks:", error);
          setFormError("Failed to load supported networks.");
        }
      } finally {
        if (!cancelled) {
          setLoadingNetworks(false);
        }
      }
    }

    loadNetworks();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!subjectEditedManually) {
      if (supportType === "problem") {
        setSubject(
          `Deployment issue on ${selectedNetworkLabel || "selected network"}`,
        );
      } else if (supportType === "billing") {
        setSubject("Request Pro access / upgrade help");
      } else if (supportType === "feature") {
        setSubject("Feature request for Krionex");
      } else if (supportType === "question") {
        setSubject("Need help using Krionex");
      }
    }

    if (supportType === "billing") {
      setNetwork("");
      setTokenType("");
      setTokenName("");
      setTokenSymbol("");
      setContractAddress("");
      setDeploymentId("");
      setTransactionHash("");
      setTokenId("");
      setAmount("");
      setDecimals("");
      setBaseURI("");
      setAdvancedData("0x");
    }
  }, [supportType, selectedNetworkLabel, subjectEditedManually]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const intent = params.get("intent");

    if (intent === "pro-access") {
      setSupportType("billing");
      setSubject("Request Pro access / upgrade help");
      setSubjectEditedManually(false);
      setDescription(
        "I would like to upgrade my Krionex plan and get access to Pro features. Please help me with pricing, access, or next steps.",
      );
      setNetwork("");
      setTokenType("");
      setTokenName("");
      setTokenSymbol("");
      setContractAddress("");
      setDeploymentId("");
      setTransactionHash("");
      setTokenId("");
      setAmount("");
      setDecimals("");
      setBaseURI("");
      setAdvancedData("0x");
    }
  }, [location.search]);

  async function loadRequests(activeWallet) {
    if (!activeWallet?.trim()) {
      setRequests([]);
      return;
    }

    try {
      setLoadingRequests(true);
      const result = await getMySupportTickets(activeWallet.trim());
      setRequests(result.data || []);
    } catch (error) {
      console.error("Failed to load support requests:", error);
    } finally {
      setLoadingRequests(false);
    }
  }

  useEffect(() => {
    if (!isAuthenticated) {
      setRequests([]);
      return;
    }

    if (walletAddress?.trim()) {
      loadRequests(walletAddress);
    } else {
      setRequests([]);
    }
  }, [walletAddress, isAuthenticated]);

  useEffect(() => {
    try {
      if (!isAuthenticated) {
        localStorage.removeItem(HELP_CENTER_DRAFT_KEY);
        return;
      }

      const draft = {
        supportType,
        walletAddress,
        walletEditedManually,
        network,
        subject,
        subjectEditedManually,
        description,
        tokenType,
        tokenName,
        tokenSymbol,
        contractAddress,
        deploymentId,
        transactionHash,
        tokenId,
        amount,
        decimals,
        baseURI,
        advancedData,
      };

      localStorage.setItem(HELP_CENTER_DRAFT_KEY, JSON.stringify(draft));
    } catch (error) {
      console.error("Failed to save Help Center draft:", error);
    }
  }, [
    isAuthenticated,
    supportType,
    walletAddress,
    walletEditedManually,
    network,
    subject,
    subjectEditedManually,
    description,
    tokenType,
    tokenName,
    tokenSymbol,
    contractAddress,
    deploymentId,
    transactionHash,
    tokenId,
    amount,
    decimals,
    baseURI,
    advancedData,
  ]);

  function getNetworkLabel(networkValue) {
    const match = availableNetworks.find((item) => item.key === networkValue);
    return match ? match.label : networkValue;
  }

  function resetHelpCenterForm(options = {}) {
    const {
      keepWallet = true,
      keepSupportType = true,
      keepSuccessMessage = false,
    } = options;

    const savedWallet =
      localStorage.getItem("chainforge_wallet_address") || "";

    if (!keepSupportType) {
      setSupportType("problem");
    }

    if (keepWallet) {
      setWalletAddress(savedWallet);
      setWalletEditedManually(false);
    } else {
      setWalletAddress("");
      setWalletEditedManually(false);
    }

    setNetwork("");
    setSubject("");
    setSubjectEditedManually(false);
    setDescription("");
    setTokenType("");
    setTokenName("");
    setTokenSymbol("");
    setContractAddress("");
    setDeploymentId("");
    setTransactionHash("");
    setTokenId("");
    setAmount("");
    setDecimals("");
    setBaseURI("");
    setAdvancedData("0x");
    setFormError("");

    if (!keepSuccessMessage) {
      setSuccessMessage("");
    }

    localStorage.removeItem(HELP_CENTER_DRAFT_KEY);
  }

  function validateForm() {
    if (!walletAddress.trim()) {
      return "Wallet address is required.";
    }

    if (!isBillingSupport && !network.trim()) {
      return "Network is required.";
    }

    if (!subject.trim() || subject.trim().length < 5) {
      return "Subject must be at least 5 characters.";
    }

    if (!description.trim() || description.trim().length < 20) {
      return "Please provide more detail so support can help you faster.";
    }

    if (supportType === "problem") {
      if (!tokenType) {
        return "Token standard is required for problem reports.";
      }

      const hasPrimaryIdentifier =
        contractAddress.trim() || deploymentId.trim() || transactionHash.trim();

      if (!hasPrimaryIdentifier) {
        return "Provide at least one of contract address, deployment ID, or transaction hash.";
      }

      if (!tokenName.trim()) {
        return "Token name is required for this problem report.";
      }

      if (
        (tokenType === "ERC20" || tokenType === "ERC721") &&
        !tokenSymbol.trim()
      ) {
        return "Token symbol is required for ERC20 and ERC721 problem reports.";
      }

      if (tokenType === "ERC20" && amount.trim() && Number.isNaN(Number(amount))) {
        return "Amount must be a valid number.";
      }

      if (
        (tokenType === "ERC721" || tokenType === "ERC1155") &&
        tokenId.trim() &&
        !/^[0-9]+$/.test(tokenId.trim())
      ) {
        return "Token ID must be a whole number.";
      }

      if (
        tokenType === "ERC1155" &&
        amount.trim() &&
        !/^[0-9]+$/.test(amount.trim())
      ) {
        return "ERC1155 amount must be a whole number.";
      }

      if (tokenType === "ERC20" && decimals.trim()) {
        const parsed = Number(decimals.trim());
        if (!Number.isInteger(parsed) || parsed < 0 || parsed > 18) {
          return "Decimals must be an integer between 0 and 18.";
        }
      }

      if (
        tokenType === "ERC1155" &&
        advancedData.trim() &&
        !/^0x([a-fA-F0-9]{2})*$/.test(advancedData.trim())
      ) {
        return "Advanced data must be valid hex starting with 0x.";
      }
    }

    return "";
  }

  function buildPayload() {
    const finalDescription = buildSupportDescription({
      userDescription: description,
      networkLabel: selectedNetworkLabel,
      tokenType,
      tokenName,
      tokenSymbol,
      contractAddress,
      deploymentId,
      transactionHash,
      tokenId,
      amount,
      decimals,
      baseURI,
      advancedData,
    });

    return {
      walletAddress: walletAddress.trim(),
      network: network.trim(),
      deploymentId: normalizeOptional(deploymentId),
      transactionHash: normalizeOptional(transactionHash),

      contractAddress: normalizeOptional(contractAddress),
      tokenStandard: normalizeOptional(tokenType),
      tokenName: normalizeOptional(tokenName),
      tokenSymbol: normalizeOptional(tokenSymbol),

      issueType: selectedSupportType.issueType,
      priority: selectedSupportType.defaultPriority,
      subject: subject.trim(),
      description: finalDescription,
    };
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setFormError("");
    setSuccessMessage("");

    const validationMessage = validateForm();
    if (validationMessage) {
      setFormError(validationMessage);
      return;
    }

    try {
      setSubmitting(true);

      const result = await createSupportTicket(buildPayload());
      const created = result.data;

      setSuccessMessage(
        `Your request has been submitted successfully. Reference ID: ${created.publicId}`,
      );

      resetHelpCenterForm({
        keepWallet: true,
        keepSupportType: true,
        keepSuccessMessage: true,
      });

      await loadRequests(walletAddress);
    } catch (error) {
      console.error("Failed to submit support request:", error);
      setFormError(error.message || "Failed to submit support request.");
    } finally {
      setSubmitting(false);
    }
  }

  function renderDescriptionLabel() {
    if (supportType === "problem") return "What happened?";
    if (supportType === "feature") return "What would you like to improve?";
    return "How can we help?";
  }

  function renderDescriptionPlaceholder() {
    if (supportType === "problem") {
      return "Describe the issue, what you were trying to do, what happened instead, and any error you saw.";
    }
    if (supportType === "feature") {
      return "Describe the feature idea, why it matters, and how it would improve your workflow.";
    }
    return "Describe your question or what you need help with.";
  }

  const showLoggedOutNotice = !isAuthenticated;

  return (
    <div className="min-h-screen bg-[#F7F8FA] px-4 py-8 text-slate-950">
      <div className="pointer-events-none fixed inset-0 -z-10 bg-[radial-gradient(circle_at_top_left,_rgba(197,155,69,0.10),_transparent_30%),radial-gradient(circle_at_top_right,_rgba(5,7,19,0.08),_transparent_30%),linear-gradient(to_bottom,_#F7F8FA,_#F2F3F5_45%,_#F7F8FA)]" />

      <div className="mx-auto max-w-7xl space-y-6">
        <div className="relative overflow-hidden rounded-[32px] border border-[#2A2418] bg-[#050713] p-6 text-white shadow-[0_24px_70px_rgba(5,7,19,0.28)] sm:p-8">
  <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(197,155,69,0.24),_transparent_30%),radial-gradient(circle_at_bottom_right,_rgba(255,255,255,0.08),_transparent_32%)]" />
  <div className="absolute inset-0 bg-gradient-to-r from-[#050713] via-[#050713]/95 to-[#111827]/90" />
  <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#E6C978]/60 to-transparent" />

          <div className="relative">
            <div className="flex flex-col gap-7 xl:flex-row xl:items-start xl:justify-between">
              <div className="max-w-3xl">
                <div className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.06] px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-[#E6C978]">
                  Krionex Support
                </div>

                <h1 className="mt-5 text-3xl font-semibold tracking-tight text-white sm:text-4xl">
                  Support for deployments, billing, and workspace operations.
                </h1>

                <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-300 sm:text-base">
                  Submit a structured support request with wallet, network, contract,
                  and transaction context so issues can be reviewed faster.
                </p>

                <div className="mt-6 flex flex-wrap gap-2">
                  <Badge tone="brand">{selectedSupportType.title}</Badge>
                  {isAuthenticated ? (
                    <Badge tone="success">Signed in</Badge>
                  ) : (
                    <Badge tone="warning">Sign in required</Badge>
                  )}
                  {walletAddress?.trim() ? (
                    <Badge tone="success">Wallet linked</Badge>
                  ) : (
                    <Badge tone="neutral">Wallet pending</Badge>
                  )}
                </div>
              </div>

              <div className="grid w-full gap-3 sm:grid-cols-2 xl:max-w-md">
                <div className="rounded-3xl border border-white/10 bg-white/[0.06] p-5 backdrop-blur">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                    Request Type
                  </div>
                  <div className="mt-3 text-lg font-semibold tracking-tight text-white">
                    {selectedSupportType.title}
                  </div>
                  <p className="mt-2 text-xs leading-5 text-slate-400">
                    Structured routing for faster review.
                  </p>
                </div>

                <div className="rounded-3xl border border-white/10 bg-white/[0.06] p-5 backdrop-blur">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                    My Requests
                  </div>
                  <div className="mt-3 text-lg font-semibold tracking-tight text-white">
                    {walletAddress?.trim() ? requests.length : 0}
                  </div>
                  <p className="mt-2 text-xs leading-5 text-slate-400">
                    Requests linked to your wallet.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {SUPPORT_TYPES.map((item) => (
            <SupportTypeCard
              key={item.key}
              item={item}
              active={item.key === supportType}
              onClick={() => setSupportType(item.key)}
            />
          ))}
        </div>

        <div className="grid gap-6 xl:grid-cols-[minmax(0,1.35fr)_420px]">
          <SectionCard
            title="Contact Support"
            subtitle="Share the minimum context needed to identify the wallet, network, asset, and workflow involved."
          >
            <form onSubmit={handleSubmit} className="space-y-5">
              {showLoggedOutNotice ? (
                <div className="mb-4">
                  <InlineNotice
                    title="Sign to continue"
                    subtitle="Please sign in with your  wallet to submit or review support requests."
                    tone="warning"
                  />
                </div>
              ) : null}
              <div className="grid gap-4 md:grid-cols-2">
                <div className="md:col-span-2">
                  <Label>Wallet Address</Label>
                  <Input
                    type="text"
                    value={walletAddress}
                    onChange={(e) => {
                      setWalletEditedManually(true);
                      setWalletAddress(e.target.value);
                    }}
                    placeholder="0x..."
                  />
                  <p className="mt-2 text-xs leading-5 text-slate-500">
                    This field is auto-filled from your signed-in or last connected Krionex wallet when available.
                    You can edit it if this request relates to a different wallet address.
                  </p>
                </div>

                {!isBillingSupport ? (
                  <div>
                    <Label>Network</Label>
                    <Select
                      value={network}
                      onChange={(e) => setNetwork(e.target.value)}
                      disabled={loadingNetworks || submitting}
                    >
                      <option value="">
                        {loadingNetworks
                          ? "Loading networks..."
                          : availableNetworks.length === 0
                            ? "No supported networks available"
                            : "Select network"}
                      </option>
                      {availableNetworks.map((item) => (
                        <option key={item.key} value={item.key}>
                          {item.label}
                        </option>
                      ))}
                    </Select>
                  </div>
                ) : null}

                <div className={isBillingSupport ? "md:col-span-2" : ""}>
                  <Label>Request Type</Label>
                  <Input type="text" value={selectedSupportType.title} readOnly />
                </div>
              </div>

              <div>
                <Label>Subject</Label>
                <Input
                  type="text"
                  value={subject}
                  onChange={(e) => {
                    setSubjectEditedManually(true);
                    setSubject(e.target.value);
                  }}
                  placeholder="Brief summary of your request"
                />
              </div>

              <div>
                <Label>{renderDescriptionLabel()}</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={renderDescriptionPlaceholder()}
                />
              </div>

              {!isBillingSupport ? (
                <div className="space-y-4">
                  <div className="border-t border-slate-200 pt-4" />
                  <div>
                    <div className="text-base font-semibold text-slate-900">
                      Technical Context
                    </div>
                    <p className="mt-1 text-sm leading-6 text-slate-500">
                      Add the deployment identifiers and token details from Deployments, History, or Token Dashboard so support can trace the exact asset and transaction quickly.
                    </p>
                  </div>

                  <div className="grid gap-4 md:grid-cols-3">
                    <div>
                      <Label>Token Standard *</Label>
                      <Select
                        value={tokenType}
                        onChange={(e) => setTokenType(e.target.value)}
                      >
                        {TOKEN_TYPE_OPTIONS.map((item) => (
                          <option key={item.value || "empty"} value={item.value}>
                            {item.label}
                          </option>
                        ))}
                      </Select>
                    </div>

                    <div>
                      <Label>Token Name *</Label>
                      <Input
                        type="text"
                        value={tokenName}
                        onChange={(e) => setTokenName(e.target.value)}
                        placeholder="MyToken"
                      />
                    </div>

                    {(tokenType === "ERC20" || tokenType === "ERC721") ? (
                      <div>
                        <Label>Token Symbol *</Label>
                        <Input
                          type="text"
                          value={tokenSymbol}
                          onChange={(e) => setTokenSymbol(e.target.value)}
                          placeholder="MTK"
                        />
                      </div>
                    ) : null}
                  </div>

                  <div className="grid gap-4 md:grid-cols-3">
                    <div>
                      <Label>Contract Address</Label>
                      <Input
                        type="text"
                        value={contractAddress}
                        onChange={(e) => setContractAddress(e.target.value)}
                        placeholder="0x..."
                      />
                    </div>

                    <div>
                      <Label>Deployment ID</Label>
                      <Input
                        type="text"
                        value={deploymentId}
                        onChange={(e) => setDeploymentId(e.target.value)}
                        placeholder="dep_123"
                      />
                    </div>

                    <div>
                      <Label>Transaction Hash</Label>
                      <Input
                        type="text"
                        value={transactionHash}
                        onChange={(e) => setTransactionHash(e.target.value)}
                        placeholder="0x..."
                      />
                    </div>
                  </div>

                  {tokenType === "ERC20" ? (
                    <div className="grid gap-4 md:grid-cols-3">
                      <div>
                        <Label>Amount Involved</Label>
                        <Input
                          type="text"
                          inputMode="decimal"
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          placeholder="e.g. 1000"
                        />
                      </div>

                      <div>
                        <Label>Decimals</Label>
                        <Input
                          type="number"
                          min="0"
                          max="18"
                          step="1"
                          value={decimals}
                          onChange={(e) => setDecimals(e.target.value)}
                          placeholder="18"
                        />
                      </div>
                    </div>
                  ) : null}

                  {tokenType === "ERC721" ? (
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <Label>Token ID</Label>
                        <Input
                          type="text"
                          value={tokenId}
                          onChange={(e) => setTokenId(e.target.value)}
                          placeholder="e.g. 1"
                        />
                      </div>

                      <div>
                        <Label>Metadata Base URI</Label>
                        <Input
                          type="text"
                          value={baseURI}
                          onChange={(e) => setBaseURI(e.target.value)}
                          placeholder="ipfs://YOUR_METADATA_CID/"
                        />
                      </div>
                    </div>
                  ) : null}

                  {tokenType === "ERC1155" ? (
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <Label>Token ID</Label>
                        <Input
                          type="text"
                          value={tokenId}
                          onChange={(e) => setTokenId(e.target.value)}
                          placeholder="e.g. 1"
                        />
                      </div>

                      <div>
                        <Label>Amount</Label>
                        <Input
                          type="text"
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          placeholder="e.g. 10"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <Label>Base URI / Metadata Template</Label>
                        <Input
                          type="text"
                          value={baseURI}
                          onChange={(e) => setBaseURI(e.target.value)}
                          placeholder="ipfs://YOUR_METADATA_CID/{id}.json"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <Label>Advanced Data</Label>
                        <Input
                          type="text"
                          value={advancedData}
                          onChange={(e) => setAdvancedData(e.target.value)}
                          placeholder="0x"
                        />
                      </div>
                    </div>
                  ) : null}

                  {supportType === "problem" ? (
                    <InlineNotice
                      title="Problem reports need a primary identifier"
                      subtitle="Provide at least one of contract address, deployment ID, or transaction hash. Add token-specific fields below when they are relevant to the issue."
                      tone="warning"
                    />
                  ) : null}
                </div>
              ) : null}

              {formError ? (
                <InlineNotice
                  title="Unable to submit request"
                  subtitle={formError}
                  tone="error"
                />
              ) : null}

              {successMessage ? (
                <InlineNotice
                  title="Request submitted successfully"
                  subtitle={successMessage}
                  tone="success"
                />
              ) : null}

              <div className="flex flex-wrap gap-3">
                <PrimaryButton
                  type="submit"
                  disabled={
                    !isAuthenticated ||
                    submitting ||
                    (!isBillingSupport && loadingNetworks) ||
                    (!isBillingSupport && !network)
                  }
                  className="w-full sm:w-auto sm:min-w-[180px]"
                >
                  {submitting ? "Submitting..." : "Submit Request"}
                </PrimaryButton>

                <button
                  type="button"
                  onClick={() =>
                    resetHelpCenterForm({
                      keepWallet: true,
                      keepSupportType: true,
                      keepSuccessMessage: false,
                    })
                  }
                  className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-[#E7D8AC] hover:bg-[#FFF9EA] hover:text-slate-950"
                >
                  Clear Form
                </button>

                <button
                  type="button"
                  onClick={() => {
                    const currentSupportType = supportType;
                    resetHelpCenterForm({
                      keepWallet: true,
                      keepSupportType: false,
                      keepSuccessMessage: false,
                    });
                    setSupportType(currentSupportType);
                  }}
                  className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-[#E7D8AC] hover:bg-[#FFF9EA] hover:text-slate-950"
                >
                  New Request
                </button>

                {!isBillingSupport ? (
                  <button
                    type="button"
                    onClick={() => {
                      setTokenType("");
                      setTokenName("");
                      setTokenSymbol("");
                      setContractAddress("");
                      setDeploymentId("");
                      setTransactionHash("");
                      setTokenId("");
                      setAmount("");
                      setDecimals("");
                      setBaseURI("");
                      setAdvancedData("0x");
                      setFormError("");
                      setSuccessMessage("");
                    }}
                    className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-[#E7D8AC] hover:bg-[#FFF9EA] hover:text-slate-950"
                  >
                    Another Token
                  </button>
                ) : null}
              </div>
            </form>
          </SectionCard>

          <SectionCard
            title="My Requests"
            subtitle="Review recent support requests tied to your wallet and continue the conversation."
            right={
              walletAddress?.trim() ? (
                <Badge tone="brand">{requests.length} request(s)</Badge>
              ) : null
            }
          >
            {!isAuthenticated ? (
              <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-6 text-sm leading-6 text-slate-500">
                Sign in with your Krionex wallet to view your support history.
              </div>
            ) : !walletAddress?.trim() ? (
              <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-6 text-sm leading-6 text-slate-500">
                Enter your wallet address to view your support history.
              </div>
            ) : loadingRequests ? (
              <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-6 text-sm leading-6 text-slate-500">
                Loading your requests...
              </div>
            ) : requests.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-6 text-sm leading-6 text-slate-500">
                No support requests found yet.
              </div>
            ) : (
              <div className="space-y-3">
                {requests.map((request) => (
                  <a
                    key={request.id}
                    href={`/help-center/${request.publicId}?wallet=${encodeURIComponent(
                      walletAddress,
                    )}`}
                    className="block rounded-3xl border border-slate-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:border-[#E7D8AC] hover:shadow-[0_18px_45px_rgba(15,23,42,0.08)]"
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#8A6A1F]">
                          {request.publicId}
                        </div>
                        <div className="mt-2 text-sm font-semibold text-slate-900">
                          {request.subject}
                        </div>
                        <div className="mt-2 text-xs leading-5 text-slate-500">
                          {humanizeIssueType(request.issueType)} •{" "}
                          {getNetworkLabel(request.network)} •{" "}
                          {formatDate(request.createdAt)}
                        </div>
                      </div>

                      <Badge
                        tone={
                          request.status === "RESOLVED"
                            ? "success"
                            : request.status === "WAITING_FOR_USER"
                              ? "warning"
                              : request.status === "IN_REVIEW"
                                ? "brand"
                                : "neutral"
                        }
                      >
                        {humanizeStatus(request.status)}
                      </Badge>
                    </div>
                  </a>
                ))}
              </div>
            )}
          </SectionCard>
        </div>

        <Surface className="overflow-hidden border-[#1F2937]">
          <div className="bg-[#050713] p-6 text-white">
            <div className="text-xs font-semibold uppercase tracking-[0.16em] text-[#E6C978]">
              Billing support
            </div>

            <div className="mt-3 text-xl font-semibold tracking-tight">
              Plan, payment, and access guidance
            </div>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-white/75">
              Use billing support for Pro access, pricing questions, payment issues,
              or account upgrade guidance.
            </p>

            <div className="mt-5 flex flex-wrap gap-3">
              <Link
                to="/upgrade"
                className="inline-flex items-center justify-center rounded-2xl bg-white px-4 py-3 text-sm font-semibold text-slate-950 transition hover:bg-[#FFF9EA]"
              >
                View Plans
              </Link>

              <button
                type="button"
                onClick={() => setSupportType("billing")}
                className="inline-flex items-center justify-center rounded-2xl border border-white/15 px-4 py-3 text-sm font-semibold text-white transition hover:border-[#E6C978]/70 hover:bg-white/10"
              >
                Switch to Billing Help
              </button>
            </div>
          </div>
        </Surface>
      </div>
    </div>
  );
}